import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Spin, Empty, Space, Tag, Divider } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, CheckCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatMontant } from '../../variables/constants';
import DetailCalculOffre from './components/DetailCalculOffre';
import { ouvrirImpressionOffre } from './components/WorkflowOffre';
import {
  ResumeOffreHomologue, TableauOffreBlesse, TableauOffreDeces,
} from './components/OngletOffresIndemnisation';
import { localeIntl } from '../../i18n/format';

/**
 * Détail de l'offre d'indemnisation d'une victime, en pleine page.
 *
 * Le détail s'ouvrait dans un tiroir de 640 px : le tableau des chefs de préjudice et
 * l'explication du calcul y tenaient mal, et la page ne pouvait ni se partager par son
 * adresse, ni s'imprimer telle quelle. « Imprimer » sort désormais le document envoyé à
 * la victime, et non un second document qui lui ressemblait de loin.
 */
export default function OffreDetail() {
  const { t } = useTranslation('sinistres');
  const { id: sinistreId, victimeId } = useParams();
  const navigate = useNavigate();

  const [sinistre, setSinistre] = useState(null);
  const [victime, setVictime] = useState(null);
  const [offre, setOffre] = useState(null);
  const [expertise, setExpertise] = useState(null);
  const [ayantsDroit, setAyantsDroit] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [validation, setValidation] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [sin, vic, off] = await Promise.all([
        axiosInstance.get(`/v1/sinistres/${sinistreId}`).then((r) => r.data?.data).catch(() => null),
        axiosInstance.get(`/v1/victimes/${victimeId}`).then((r) => r.data?.data).catch(() => null),
        axiosInstance.get(`/v1/indemnisation/offre/victime/${victimeId}`, { skipErrorToast: true })
          .then((r) => r.data?.data).catch(() => null),
      ]);
      setSinistre(sin);
      setVictime(vic);
      setOffre(off);
      if (vic?.typeVictime === 'DECEDE') {
        const ad = await axiosInstance.get(`/v1/indemnisation/ayants-droit/victime/${victimeId}`, { skipErrorToast: true })
          .then((r) => r.data?.data || []).catch(() => []);
        setAyantsDroit(ad);
      } else {
        const exps = await axiosInstance.get(`/v1/expertises-medicales/victime/${victimeId}`, { skipErrorToast: true })
          .then((r) => r.data?.data || []).catch(() => []);
        setExpertise([...exps].sort((a, b) => new Date(b.dateRapport || 0) - new Date(a.dateRapport || 0))[0] || null);
      }
    } finally {
      setChargement(false);
    }
  }, [sinistreId, victimeId]);

  useEffect(() => { charger(); }, [charger]);

  const valider = async () => {
    if (!offre?.offreTrackingId) return;
    setValidation(true);
    try {
      await axiosInstance.patch(`/v1/indemnisation/offre/${offre.offreTrackingId}/valider`);
      toast.success(t('offreDetail.messages.offreValidee'));
      await charger();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('offreDetail.messages.erreurValidation'));
    } finally {
      setValidation(false);
    }
  };

  const estDecede = victime?.typeVictime === 'DECEDE';
  const nom = `${victime?.nom || ''} ${victime?.prenoms || ''}`.trim() || t('offreDetail.victime');

  return (
    <Layout title={t('offreDetail.titre')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} type="text"
          onClick={() => navigate(`/sinistres/${sinistreId}?tab=offres`)} />
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 19, fontWeight: 700 }}>{t('offreDetail.titreVictime', { nom })}</h1>
          <div style={{ fontSize: 12.5, color: '#6b7280' }}>
            {t('offreDetail.sinistre', { numero: sinistre?.numeroSinistreLocal || '-' })}
            {offre?.montantTotalOffre != null && <> <Trans t={t} i18nKey="offreDetail.offreDe" values={{ montant: formatMontant(offre.montantTotalOffre) }} components={{ 1: <strong /> }} /></>}
          </div>
        </div>
        <Tag color={estDecede ? 'red' : 'orange'} style={{ borderRadius: 20 }}>
          {estDecede ? t('offreDetail.decede') : t('offreDetail.blesse')}
        </Tag>
        <Space style={{ marginLeft: 'auto' }}>
          {offre && (
            <Button icon={<PrinterOutlined />}
              onClick={() => ouvrirImpressionOffre(victime, sinistre, offre, 'victime', expertise, ayantsDroit)}>
              {t('offreDetail.imprimerOffreVictime')}
            </Button>
          )}
          {offre && !offre.dateValidation && (
            <Button type="primary" icon={<CheckCircleOutlined />} loading={validation} onClick={valider}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {t('offreDetail.valider')}
            </Button>
          )}
        </Space>
      </div>

      <div className="cb-card">
        {chargement && <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>}
        {!chargement && !offre && (
          <Empty description={t('offreDetail.aucuneOffre')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        {!chargement && offre && (
          <>
            <ResumeOffreHomologue offre={offre} sinistre={sinistre} />
            {offre.origineOffre !== 'HOMOLOGUE' && (estDecede
              ? <TableauOffreDeces offre={offre} ayantsDroit={ayantsDroit} />
              : <TableauOffreBlesse offre={offre} />)}
            {(offre.origineOffre !== 'HOMOLOGUE' || offre.detailCalcul) && (
              <DetailCalculOffre detail={offre.detailCalcul} />
            )}
            <Divider />
            <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              {t('offreDetail.calculeeLe', { date: offre.createdAt ? new Date(offre.createdAt).toLocaleDateString(localeIntl()) : '-' })}
            </span>
          </>
        )}
      </div>
    </Layout>
  );
}
